import React, { useState, useEffect, useContext, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useReactToPrint } from 'react-to-print';
import axios from 'axios';
import {
    BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell
} from 'recharts';
import SherDineNav from './SherDineNav';
import SideNav from './SideNav';
import TopRootDiv from './TopRootDiv';
import { DBContext } from './DBContext';
import { exportToCSV } from '../utils/exportUtils';

// ─── helpers ────────────────────────────────────────────────────────────────

const fmt = (v) => `₹${parseFloat(v || 0).toFixed(2)}`;
const todayISO = () => new Date().toISOString().slice(0, 10);

const CHART_COLORS = [
    '#0008B4', '#57BEB5', '#AE59DC', '#E8821A',
    '#63B946', '#c30000', '#2D7DD2', '#F5A623',
    '#4ECDC4', '#FF6B6B',
];

// ─── CSV column definitions ──────────────────────────────────────────────────
const ITEM_COLS = [
    { key: 'rank', label: 'Rank' },
    { key: 'categoryName', label: 'Category' },
    { key: 'ItemName', label: 'Item' },
    { key: 'qty', label: 'Qty Sold' },
    { key: 'totalAmount', label: 'Gross (₹)' },
    { key: 'netAmount', label: 'Net (₹)' },
    { key: 'taxAmount', label: 'Tax (₹)' },
    { key: 'growthPercentage', label: 'Growth (%)' },
];

// ─── PrintView ───────────────────────────────────────────────────────────────
const PrintView = React.forwardRef(({ data, summaryData, dates }, ref) => {
    const subtitle = dates.sdate && dates.edate
        ? `${dates.sdate}  →  ${dates.edate}`
        : 'Today';

    const totalNet = summaryData ? parseFloat(summaryData.totalNetRevenue || 0) : data.reduce((s, r) => s + parseFloat(r.netAmount || 0), 0);
    const totalQty = summaryData ? parseFloat(summaryData.totalItems || 0) : data.reduce((s, r) => s + parseFloat(r.qty || 0), 0);
    const totalTax = summaryData ? parseFloat(summaryData.totalTaxes || 0) : data.reduce((s, r) => s + parseFloat(r.taxAmount || 0), 0);
    const totalBills = summaryData ? parseInt(summaryData.totalBills || 0) : 0;

    return (
        <div ref={ref} style={{ padding: '20px', fontFamily: 'Arial, sans-serif', fontSize: '11px' }}>
            <h2 style={{ fontSize: '16px', fontWeight: '700', marginBottom: '4px' }}>Item Aggregated Report</h2>
            <p style={{ color: '#666', marginBottom: '14px' }}>{subtitle}</p>

            {/* Summary */}
            <div style={{ display: 'flex', gap: '12px', marginBottom: '16px', flexWrap: 'wrap' }}>
                {[
                    ['Total Items', data.length],
                    ['Qty Sold', totalQty],
                    ['Net Revenue', fmt(totalNet)],
                    ['Total Tax', fmt(totalTax)],
                    ['Bills', totalBills],
                ].map(([label, value]) => (
                    <div key={label} style={{ background: '#f5f5f5', borderRadius: '6px', padding: '6px 14px', minWidth: '100px' }}>
                        <div style={{ fontSize: '9px', color: '#888', textTransform: 'uppercase', letterSpacing: '0.5px' }}>{label}</div>
                        <div style={{ fontSize: '14px', fontWeight: '700', color: '#0008B4' }}>{value}</div>
                    </div>
                ))}
            </div>

            {/* Table */}
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                    <tr style={{ background: '#0008B4', color: '#fff' }}>
                        {ITEM_COLS.map(c => (
                            <th key={c.key} style={{ padding: '6px 8px', textAlign: 'left', fontSize: '10px' }}>{c.label}</th>
                        ))}
                    </tr>
                </thead>
                <tbody>
                    {data.map((row, i) => (
                        <tr key={i} style={{ background: i % 2 === 0 ? '#fff' : '#f9f9f9' }}>
                            {ITEM_COLS.map(c => (
                                <td key={c.key} style={{ padding: '5px 8px', borderBottom: '1px solid #eee', fontSize: '10px' }}>
                                    {['totalAmount', 'netAmount', 'taxAmount'].includes(c.key) ? fmt(row[c.key]) : (row[c.key] ?? '')}
                                    {c.key === 'growthPercentage' && row[c.key] !== null ? `${row[c.key]}%` : ''}
                                </td>
                            ))}
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
});

// ─── Main Component ──────────────────────────────────────────────────────────
function ItemReports() {
    const { logginedDB } = useContext(DBContext);
    const navigate = useNavigate();
    const apiBaseUrl = process.env.REACT_APP_API_BASE_URL;

    const [sideNav, setSideNav] = useState(false);
    const [falseState, setFalse] = useState(null);
    const [dates, setDates] = useState({ sdate: todayISO(), edate: todayISO() });
    const [data, setData] = useState([]);
    const [summaryData, setSummaryData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [view, setView] = useState('item'); // 'item' | 'category'

    const printRef = useRef(null);

    // ── fetch ──
    const fetchData = useCallback(async () => {
        if (!logginedDB) return;
        setLoading(true);
        try {
            const [resDetails, resSummary] = await Promise.all([
                axios.get(`${apiBaseUrl}/reports/items/aggregated/${logginedDB}?sdate=${dates.sdate}&edate=${dates.edate}&limit=10000`),
                axios.get(`${apiBaseUrl}/reports/items/aggregated/summary/${logginedDB}?sdate=${dates.sdate}&edate=${dates.edate}`)
            ]);
            setData(resDetails.data?.data || []);
            setSummaryData(resSummary.data?.summary || null);
        } catch (e) {
            console.error('Item Reports fetch error:', e);
        } finally {
            setLoading(false);
        }
    }, [logginedDB, dates, apiBaseUrl]);

    useEffect(() => { fetchData(); }, [fetchData]);

    // ── print handler ──
    const handlePrint = useReactToPrint({
        content: () => printRef.current,
        documentTitle: `Item_Aggregated_Report_${dates.sdate}_${dates.edate}`,
    });

    // ── category grouping ──
    const categoryData = React.useMemo(() => {
        const map = {};
        data.forEach(row => {
            const cat = row.categoryName || 'Uncategorised';
            if (!map[cat]) map[cat] = { category: cat, totalQty: 0, grossTotal: 0, netTotal: 0, taxTotal: 0, items: [] };
            map[cat].totalQty += parseFloat(row.qty || 0);
            map[cat].grossTotal += parseFloat(row.totalAmount || 0);
            map[cat].netTotal += parseFloat(row.netAmount || 0);
            map[cat].taxTotal += parseFloat(row.taxAmount || 0);
            map[cat].items.push(row);
        });
        return Object.values(map).sort((a, b) => b.netTotal - a.netTotal);
    }, [data]);

    // ── summaries ──
    const totalNet = summaryData ? parseFloat(summaryData.totalNetRevenue || 0) : data.reduce((s, r) => s + parseFloat(r.netAmount || 0), 0);
    const totalQty = summaryData ? parseFloat(summaryData.totalItems || 0) : data.reduce((s, r) => s + parseFloat(r.qty || 0), 0);
    const totalTax = summaryData ? parseFloat(summaryData.totalTaxes || 0) : data.reduce((s, r) => s + parseFloat(r.taxAmount || 0), 0);
    const totalBills = summaryData ? parseInt(summaryData.totalBills || 0) : 0;

    // top-10 for chart
    const chartData = data.slice(0, 10).map(r => ({
        name: r.ItemName?.length > 14 ? r.ItemName.slice(0, 14) + '…' : r.ItemName,
        net: parseFloat(r.netAmount || 0),
    }));

    return (
        <div className='w-screen h-screen hidden sm:flex sm:flex-col overflow-hidden'>
            <SherDineNav sideNav={(s) => setSideNav(s)} falseState={falseState} />
            <div className='w-full bg-[#f5f5f5] flex-grow flex gap-3 overflow-hidden'>
                <div className='w-max h-full z-[9999]'>
                    <SideNav focus={'Report'} state={sideNav} falseState={(s) => setFalse(s)} />
                </div>
                <div className='w-full h-full flex flex-col pr-3 pl-0 pb-3 pt-0 gap-3 overflow-hidden'>
                    {/* Header */}
                    <div className='pr-3 h-max w-full bg-[#fff] borderBottom'>
                        <TopRootDiv image={'reports'} root={['Report', 'Item Aggregated Reports']} />
                    </div>

                    <div className='w-full h-full overflow-auto p-3 flex flex-col gap-3 font-nunito'>

                        {/* Controls bar */}
                        <div className='w-full flex flex-wrap gap-2 items-end bg-white rounded-xl p-3 shadow-sm border border-[#EBEBEB]'>
                            <div className='flex flex-col gap-1'>
                                <label className='text-[.7rem] text-[#888] font-semibold uppercase tracking-wide'>From</label>
                                <input type='date' value={dates.sdate}
                                    onChange={e => setDates(d => ({ ...d, sdate: e.target.value }))}
                                    className='border border-[#D5D5D5] rounded-lg px-3 py-1.5 text-[.85rem] focus:outline-none focus:ring-2 focus:ring-[#0008B4]/30' />
                            </div>
                            <div className='flex flex-col gap-1'>
                                <label className='text-[.7rem] text-[#888] font-semibold uppercase tracking-wide'>To</label>
                                <input type='date' value={dates.edate}
                                    onChange={e => setDates(d => ({ ...d, edate: e.target.value }))}
                                    className='border border-[#D5D5D5] rounded-lg px-3 py-1.5 text-[.85rem] focus:outline-none focus:ring-2 focus:ring-[#0008B4]/30' />
                            </div>
                            <button onClick={fetchData}
                                className='px-5 py-1.5 bg-[#0008B4] text-white rounded-lg text-[.85rem] font-semibold hover:bg-[#0006a0] transition-colors'>
                                Search
                            </button>
                            <div className='flex-1' />

                            {/* View toggle */}
                            <div className='flex gap-1 bg-[#f5f5f5] rounded-lg p-1'>
                                {['item', 'category'].map(v => (
                                    <button key={v} onClick={() => setView(v)}
                                        className={`px-4 py-1 rounded-md text-[.8rem] font-semibold transition-all ${view === v ? 'bg-white shadow text-[#0008B4]' : 'text-[#888]'}`}>
                                        {v === 'item' ? 'By Item' : 'By Category'}
                                    </button>
                                ))}
                            </div>

                            {/* Export buttons */}
                            <button onClick={() => exportToCSV(`Item_Aggregated_Report_${dates.sdate}_${dates.edate}`, ITEM_COLS, data)}
                                className='px-4 py-1.5 border border-[#D5D5D5] rounded-lg text-[.8rem] font-semibold text-[#464255] hover:bg-[#f5f5f5] transition-colors flex items-center gap-1'>
                                ↓ CSV
                            </button>
                            <button onClick={handlePrint}
                                className='px-4 py-1.5 border border-[#D5D5D5] rounded-lg text-[.8rem] font-semibold text-[#464255] hover:bg-[#f5f5f5] transition-colors flex items-center gap-1'>
                                🖨 Print / PDF
                            </button>
                        </div>

                        {/* Summary cards */}
                        <div className='grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-5 gap-3'>
                            {[
                                { label: 'Total Items', value: data.length, color: '#0008B4' },
                                { label: 'Qty Sold', value: totalQty, color: '#57BEB5' },
                                { label: 'Net Revenue', value: fmt(totalNet), color: '#0008B4' },
                                { label: 'Total Tax', value: fmt(totalTax), color: '#AE59DC' },
                                { label: 'Bills', value: totalBills, color: '#63B946' },
                            ].map(({ label, value, color }) => (
                                <div key={label} className='bg-white rounded-xl p-4 shadow-sm border border-[#EBEBEB] flex flex-col gap-1'>
                                    <span className='text-[.7rem] text-[#888] font-semibold uppercase tracking-wide'>{label}</span>
                                    <span className='text-[1.2rem] font-bold' style={{ color }}>{value}</span>
                                </div>
                            ))}
                        </div>

                        {/* Top-10 chart */}
                        {/* {data.length > 0 && (
                            <div className='bg-white rounded-xl p-4 shadow-sm border border-[#EBEBEB]'>
                                <span className='text-[.85rem] font-semibold text-[#464255]'>Top 10 Items by Net Revenue</span>
                                <div className='mt-3 h-[180px]'>
                                    <ResponsiveContainer width='100%' height='100%'>
                                        <BarChart data={chartData} margin={{ top: 0, right: 8, bottom: 0, left: 8 }}>
                                            <CartesianGrid strokeDasharray='3 3' vertical={false} stroke='#f0f0f0' />
                                            <XAxis dataKey='name' tick={{ fontSize: 9, fill: '#888' }} />
                                            <YAxis tickFormatter={v => v >= 1000 ? `${(v / 1000).toFixed(0)}k` : v} tick={{ fontSize: 9, fill: '#888' }} />
                                            <Tooltip formatter={(v) => [fmt(v), 'Net Revenue']} />
                                            <Bar dataKey='net' radius={[4, 4, 0, 0]}>
                                                {chartData.map((_, i) => <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />)}
                                            </Bar>
                                        </BarChart>
                                    </ResponsiveContainer>
                                </div>
                            </div>
                        )} */}

                        {/* Table area */}
                        <div className='bg-white rounded-xl shadow-sm border border-[#EBEBEB] overflow-hidden flex-1 min-h-0'>
                            {loading ? (
                                <div className='w-full h-48 flex items-center justify-center text-[#888] text-[.9rem]'>Loading…</div>
                            ) : data.length === 0 ? (
                                <div className='w-full h-48 flex items-center justify-center text-[#888] text-[.9rem]'>No data for selected range</div>
                            ) : view === 'item' ? (
                                <div className='overflow-auto h-full'>
                                    <table className='w-full text-[.82rem]'>
                                        <thead className='sticky top-0'>
                                            <tr className='bg-[#0008B4] text-white'>
                                                <th className='text-left px-4 py-2.5 font-semibold'>Rank</th>
                                                <th className='text-left px-4 py-2.5 font-semibold'>Category</th>
                                                <th className='text-left px-4 py-2.5 font-semibold'>Item</th>
                                                <th className='text-right px-4 py-2.5 font-semibold'>Qty</th>
                                                <th className='text-right px-4 py-2.5 font-semibold'>Gross</th>
                                                <th className='text-right px-4 py-2.5 font-semibold'>Net</th>
                                                <th className='text-right px-4 py-2.5 font-semibold'>Tax</th>
                                                <th className='text-right px-4 py-2.5 font-semibold'>Growth</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {data.map((row, i) => (
                                                <tr key={i} className={i % 2 === 0 ? 'bg-white' : 'bg-[#f9f9f9]'}>
                                                    <td className='px-4 py-2 text-[#888]'>{row.rank}</td>
                                                    <td className='px-4 py-2 text-[#464255]'>{row.categoryName || '—'}</td>
                                                    <td className='px-4 py-2 font-medium text-[#464255]'>{row.ItemName}</td>
                                                    <td className='px-4 py-2 text-right text-[#464255]'>{row.qty}</td>
                                                    <td className='px-4 py-2 text-right text-[#464255]'>{fmt(row.totalAmount)}</td>
                                                    <td className='px-4 py-2 text-right font-semibold text-[#0008B4]'>{fmt(row.netAmount)}</td>
                                                    <td className='px-4 py-2 text-right text-[#AE59DC]'>{fmt(row.taxAmount)}</td>
                                                    <td className='px-4 py-2 text-right text-[#464255]'>
                                                        {row.growthPercentage !== null ? (
                                                            <span className={row.growthPercentage >= 0 ? 'text-green-600' : 'text-red-600'}>
                                                                {row.growthPercentage > 0 ? '+' : ''}{row.growthPercentage}%
                                                            </span>
                                                        ) : '—'}
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            ) : (
                                /* Category view */
                                <div className='overflow-auto h-full'>
                                    {categoryData.map((cat, ci) => (
                                        <details key={ci} open className='border-b border-[#EBEBEB]'>
                                            <summary className='flex items-center gap-3 px-4 py-3 cursor-pointer bg-[#f5f7ff] hover:bg-[#eef0ff] select-none'>
                                                <span className='font-bold text-[#0008B4] text-[.85rem]'>{cat.category}</span>
                                                <span className='text-[.75rem] text-[#888]'>{cat.items.length} items</span>
                                                <span className='ml-auto text-[.82rem] font-semibold text-[#0008B4]'>{fmt(cat.netTotal)}</span>
                                                <span className='text-[.75rem] text-[#888]'>Qty: {cat.totalQty}</span>
                                            </summary>
                                            <table className='w-full text-[.8rem]'>
                                                <tbody>
                                                    {cat.items.map((row, i) => (
                                                        <tr key={i} className={i % 2 === 0 ? 'bg-white' : 'bg-[#fafafa]'}>
                                                            <td className='px-8 py-2 text-[#464255]'>{row.ItemName}</td>
                                                            <td className='px-4 py-2 text-right text-[#464255]'>{row.qty}</td>
                                                            <td className='px-4 py-2 text-right text-[#0008B4] font-semibold'>{fmt(row.netAmount)}</td>
                                                            <td className='px-4 py-2 text-right text-[#AE59DC]'>{fmt(row.taxAmount)}</td>
                                                            <td className='px-4 py-2 text-right text-[#888]'>
                                                                {row.growthPercentage !== null ? (
                                                                    <span className={row.growthPercentage >= 0 ? 'text-green-600' : 'text-red-600'}>
                                                                        {row.growthPercentage > 0 ? '+' : ''}{row.growthPercentage}%
                                                                    </span>
                                                                ) : ''}
                                                            </td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        </details>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            {/* Hidden print target */}
            <div className='hidden'>
                <PrintView ref={printRef} data={data} summaryData={summaryData} dates={dates} view={view} />
            </div>
        </div>
    );
}

export default ItemReports;
